// ---------------------------------------------------------------------------
// Floating bug-report button
// ---------------------------------------------------------------------------

import type { PatchPilotOffset, ResolvedConfig } from "../core/config";
import { sdkState } from "../core/state";

/** Inline SVG – simple bug/beetle icon */
const BUG_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
  <path d="M12 2a1 1 0 0 1 1 1v1.07A7.002 7.002 0 0 1 19 11v1h2a1 1 0 1 1 0 2h-2v1a6.97 6.97 0 0 1-1.08 3.72l1.78 1.78a1 1 0 0 1-1.42 1.42l-1.96-1.97A6.97 6.97 0 0 1 12 21a6.97 6.97 0 0 1-4.32-1.5l-1.96 1.92a1 1 0 0 1-1.42-1.42l1.78-1.78A6.97 6.97 0 0 1 5 15v-1H3a1 1 0 1 1 0-2h2v-1a7.002 7.002 0 0 1 6-6.93V3a1 1 0 0 1 1-1Zm0 6a5 5 0 0 0-5 5v2a5 5 0 0 0 10 0v-2a5 5 0 0 0-5-5Zm0 2a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0v-4a1 1 0 0 1 1-1Zm-3-5.46A7 7 0 0 1 7.1 6.3L5.7 4.88a1 1 0 0 1 1.42-1.42L8.54 4.9A6.93 6.93 0 0 1 9 4.54Zm6 0a6.93 6.93 0 0 1 .46.36l1.42-1.44a1 1 0 0 1 1.42 1.42L19.9 6.3A7 7 0 0 1 15 4.54Z"/>
</svg>`;

/** Reference to the current floating button for dynamic updates */
let currentBtn: HTMLButtonElement | null = null;

/** Apply offset styles to the floating button */
function applyOffset(btn: HTMLButtonElement, offset: PatchPilotOffset): void {
  if (offset.bottom !== undefined) btn.style.bottom = `${offset.bottom}px`;
  if (offset.right !== undefined) btn.style.right = `${offset.right}px`;
  if (offset.left !== undefined) btn.style.left = `${offset.left}px`;
}

/**
 * Creates the floating bug-report button and appends it to the shadow root.
 * Returns the button element for external reference.
 */
export function createFloatingButton(
  shadowRoot: ShadowRoot,
  config: ResolvedConfig,
): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.className = `pp-floating-btn pp-floating-btn--${config.position === "bottom-left" ? "left" : "right"}`;
  btn.setAttribute("type", "button");
  btn.setAttribute(
    "aria-label",
    config.locale === "en" ? "Report a bug" : "버그 리포트",
  );
  btn.innerHTML = BUG_ICON;

  applyOffset(btn, config.offset);

  btn.addEventListener("click", () => {
    if (sdkState.isOpen) {
      sdkState.isOpen = false;
      sdkState.emit("close");
    } else {
      sdkState.isOpen = true;
      sdkState.emit("open");
    }
  });

  shadowRoot.appendChild(btn);
  currentBtn = btn;

  return btn;
}

/**
 * Dynamically update the floating button position and offset
 * without full SDK re-initialization.
 */
export function updateFloatingButtonPosition(
  position: "bottom-right" | "bottom-left",
  offset: PatchPilotOffset = {},
): void {
  if (!currentBtn) return;

  // Reset position classes
  currentBtn.classList.remove("pp-floating-btn--left", "pp-floating-btn--right");
  currentBtn.classList.add(
    position === "bottom-left" ? "pp-floating-btn--left" : "pp-floating-btn--right",
  );

  // Reset inline position styles
  currentBtn.style.bottom = "";
  currentBtn.style.right = "";
  currentBtn.style.left = "";

  applyOffset(currentBtn, offset);
}

/**
 * Clear the internal button reference (called on destroy).
 */
export function clearFloatingButton(): void {
  currentBtn = null;
}
